import { reportDefaults } from '~/pages/CommercialOps/ContributionMargin/fidelity';

import type { PersonalViewState } from '~/hooks/apis/reporting/usePersonalViews';

import { captureViewState } from './viewState';

export const LEGACY_FINANCIAL_VIEWS_KEY = 'contribution-margin-views-v1';
export type LegacyView = { name: string; state: PersonalViewState };

/** Stop between writes if the initiating account or mounted report changes. */
export async function importLegacyViews(
  views: Array<LegacyView>,
  isCurrent: () => boolean,
  save: (view: LegacyView) => Promise<unknown>,
): Promise<{ imported: number; error?: unknown } | null> {
  let imported = 0;
  for (const view of views) {
    if (!isCurrent()) return null;
    try {
      await save(view);
      imported += 1;
    } catch (error) {
      return isCurrent() ? { imported, error } : null;
    }
  }
  return isCurrent() ? { imported } : null;
}

/** Import only the existing current codec. Older unprefixed financial scopes are ambiguous. */
export function compatibleLegacyViews(raw: string | null): { views: Array<LegacyView>; incompatible: number } {
  let stored: unknown;
  try {
    stored = JSON.parse(raw ?? '[]');
  } catch {
    return { views: [], incompatible: 1 };
  }
  if (!Array.isArray(stored)) return { views: [], incompatible: 1 };
  const allowed = new Set(Object.keys(reportDefaults).map((key) => `cm${key}`));
  const views: Array<LegacyView> = [];
  let incompatible = 0;
  for (const entry of stored) {
    try {
      if (
        typeof entry?.name !== 'string' ||
        !entry.name.trim() ||
        entry.name.trim().length > 80 ||
        typeof entry.search !== 'string'
      )
        throw new Error();
      const state = captureViewState('native', entry.search.startsWith('?') ? entry.search : `?${entry.search}`);
      const params = new URLSearchParams(state.search);
      if (![...params].length || [...params.keys()].some((key) => !allowed.has(key))) throw new Error();
      views.push({ name: entry.name.trim(), state });
    } catch {
      incompatible += 1;
    }
  }
  return { views, incompatible };
}
